// ─────────────────────────────────────────────
// KLOTHIQ — Customer Receipt & Voucher PDF Generator
// ─────────────────────────────────────────────
// Generates professional, branded PDF customer receipts and
// payment vouchers using jsPDF and jsPDF AutoTable.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brandConfig, getBrandLogoCanvasDataUrl } from '@/config/brandConfig';
import { COLOR, LOGO_BASE64 } from './pdfConstants';
import { formatCurrency } from './pdfHelpers';

export interface SaleItemReceiptData {
  id?: string;
  name: string;
  sku?: string;
  size?: string;
  color?: string | null;
  attributes?: string | null;
  quantity: number;
  unit_price: number;
  discount?: number;
  line_total: number;
}

export interface CustomerReceiptData {
  name: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  pincode?: string | null;
}

export interface SaleReceiptData {
  sale_number: string;
  order_number?: string;
  sale_date: string;
  sale_status: string;
  payment_status: string;
  customer?: CustomerReceiptData | null;
  items: SaleItemReceiptData[];
  subtotal: number;
  delivery_charge?: number;
  discount_total?: number;
  total_amount: number;
  paid_amount: number;
  balance_due: number | null;
  balance_note?: string;
  payment_method?: string;
  notes?: string | null;
}

export interface PaymentReceiptData {
  receipt_number: string;
  payment_date: string;
  payment_type: 'income' | 'expense' | 'sale_payment' | 'purchase_payment';
  party_name?: string | null;
  amount: number;
  payment_method: string;
  account_name?: string | null;
  reference_number?: string | null;
  particular?: string | null;
  notes?: string | null;
  sale_number?: string | null;
}

function formatRupee(val: number): string {
  // Built-in PDF fonts do not contain the rupee glyph; keep amounts readable.
  return formatCurrency(val);
}

/**
 * Builds a styled, branded PDF document for a Sale Receipt.
 */
export function buildSalesReceiptPdf(sale: SaleReceiptData): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // 1. Teal Brand Header Banner — RGB(6,182,212)
  doc.setFillColor(6, 182, 212);
  doc.rect(0, 0, 210, 26, 'F');

  // Embed the real mark so receipt PDFs do not depend on network/image loading.
  try {
    const logoUrl = LOGO_BASE64 || getBrandLogoCanvasDataUrl();
    if (logoUrl) {
      doc.addImage(logoUrl, 'PNG', 12, 4, 18, 18);
    }
  } catch (e) {
    console.warn('Could not add logo image to PDF:', e);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandConfig.name, 198, 12, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(224, 247, 250);
  doc.text(brandConfig.tagline, 198, 17, { align: 'right' });
  doc.text(
    `${brandConfig.phone}  ·  ${brandConfig.address}, ${brandConfig.cityStateZip}`,
    198,
    22,
    { align: 'right' },
  );

  // 2. Receipt Title & Reference Strip
  doc.setFillColor(248, 250, 252); // Slate 50
  doc.rect(12, 32, 186, 20, 'F');
  doc.setDrawColor(226, 232, 240); // Slate 200
  doc.rect(12, 32, 186, 20, 'S');

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('CUSTOMER SALES RECEIPT', 18, 41);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Receipt No: ${sale.sale_number}`, 18, 47);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`Date: ${sale.sale_date}`, 192, 41, { align: 'right' });

  const statusText = `Status: ${sale.sale_status.toUpperCase()} (${sale.payment_status.toUpperCase()})`;
  doc.setFontSize(8.5);
  doc.setTextColor(30, 58, 138); // Darker blue text
  doc.text(statusText, 192, 47, { align: 'right' });

  // 3. Customer Info Section
  let currentY = 58;
  doc.setFillColor(255, 255, 255);
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('Customer Details:', 14, currentY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);

  const customerName = sale.customer?.name || 'Walk-in Customer';
  doc.text(customerName, 14, currentY + 5);

  if (sale.customer?.phone) {
    doc.text(`Phone: ${sale.customer.phone}`, 14, currentY + 10);
  }

  if (sale.customer?.address || sale.customer?.city) {
    const addrParts = [sale.customer.address, sale.customer.city, sale.customer.pincode]
      .filter(Boolean)
      .join(', ');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(addrParts, 14, currentY + 15);
    currentY += 5;
  }
  currentY += 15;

  // 4. Items Table
  const tableHeaders = [
    'Product Description',
    'Variant Details',
    'Qty',
    'Unit Price',
    'Discount',
    'Amount',
  ];
  const tableRows = sale.items.map((item) => {
    const variantDesc =
      [
        item.size ? `Size: ${item.size}` : null,
        item.color ? `Color: ${item.color}` : null,
        item.attributes ? item.attributes : null,
      ]
        .filter(Boolean)
        .join(' | ') || 'Default';

    return [
      item.name,
      variantDesc,
      item.quantity.toString(),
      formatRupee(item.unit_price),
      item.discount && item.discount > 0 ? formatRupee(item.discount) : '—',
      formatRupee(item.line_total),
    ];
  });

  autoTable(doc, {
    startY: currentY,
    margin: { left: 14, right: 14 },
    head: [tableHeaders],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: [6, 182, 212], // Teal brand
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      cellPadding: 3,
    },
    bodyStyles: {
      textColor: [30, 41, 59],
      fontSize: 8,
      cellPadding: 2.5,
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { halign: 'center', cellWidth: 12 },
      3: { halign: 'right', cellWidth: 25 },
      4: { halign: 'right', cellWidth: 20 },
      5: { halign: 'right', cellWidth: 30 },
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const finalTableY = (doc as any).lastAutoTable?.finalY ?? currentY + 40;
  let totalsY = finalTableY + 8;

  // 5. Totals Breakdown Card (Right aligned)
  const boxWidth = 85;
  const boxX = 210 - 14 - boxWidth;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);

  const totalsLines = [
    { label: 'Subtotal:', value: formatRupee(sale.subtotal) },
    sale.delivery_charge
      ? { label: 'Delivery Charge:', value: formatRupee(sale.delivery_charge) }
      : null,
    sale.discount_total
      ? { label: 'Discount:', value: `-${formatRupee(sale.discount_total)}` }
      : null,
    { label: 'Grand Total:', value: formatRupee(sale.total_amount), bold: true },
    { label: 'Amount Paid:', value: formatRupee(sale.paid_amount) },
    {
      label: 'Balance Due:',
      value:
        sale.balance_note ??
        (sale.balance_due === null ? 'Not applicable' : formatRupee(sale.balance_due)),
      highlight: (sale.balance_due ?? 0) > 0,
    },
  ].filter(Boolean) as { label: string; value: string; bold?: boolean; highlight?: boolean }[];

  const boxHeight = totalsLines.length * 6 + 6;
  doc.rect(boxX, totalsY, boxWidth, boxHeight, 'FD');

  let lineY = totalsY + 6;
  doc.setFontSize(8.5);

  totalsLines.forEach((line) => {
    if (line.bold) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
    } else if (line.highlight) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(185, 28, 28); // Red for balance due
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
    }
    doc.text(line.label, boxX + 6, lineY);
    doc.text(line.value, boxX + boxWidth - 6, lineY, { align: 'right' });
    lineY += 6;
  });

  // Payment Method note on left side
  if (sale.payment_method) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Payment Method: ${sale.payment_method.toUpperCase().replace(/_/g, ' ')}`,
      14,
      totalsY + 6,
    );
  }

  if (sale.notes) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Notes: ${sale.notes}`, 14, totalsY + 12);
  }

  // 6. Branded Footer
  const pageHeight = 297;
  doc.setDrawColor(226, 232, 240);
  doc.line(14, pageHeight - 18, 196, pageHeight - 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(brandConfig.receiptFooterMessage, 105, pageHeight - 12, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(
    `${brandConfig.name} · ${brandConfig.address}, ${brandConfig.cityStateZip}`,
    105,
    pageHeight - 7,
    { align: 'center' },
  );

  return doc;
}

/**
 * Builds a styled PDF for an individual Payment Receipt / Expense Voucher.
 */
export function buildPaymentReceiptPdf(payment: PaymentReceiptData): jsPDF {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });

  const isIncome = payment.payment_type === 'income' || payment.payment_type === 'sale_payment';
  const documentTitle = isIncome ? 'PAYMENT RECEIPT' : 'EXPENSE / PAYMENT VOUCHER';

  // Premium Ink Header Banner with gold accent
  doc.setFillColor(...COLOR.ink);
  doc.rect(0, 0, 148, 20, 'F');
  doc.setFillColor(...COLOR.gold);
  doc.rect(0, 20, 148, 0.8, 'F');

  doc.addImage(LOGO_BASE64, 'PNG', 10, 4, 12, 12);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(brandConfig.name, 26, 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...COLOR.goldLight);
  doc.text(brandConfig.tagline, 26, 14);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLOR.goldLight);
  doc.text(documentTitle, 138, 13, { align: 'right' });

  // Receipt details box
  doc.setFillColor(248, 250, 252);
  doc.rect(10, 26, 128, 16, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(10, 26, 128, 16, 'S');

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text(`Receipt #: ${payment.receipt_number}`, 14, 34);
  doc.text(`Date: ${payment.payment_date}`, 134, 34, { align: 'right' });

  // Details
  let y = 50;
  const rows = [
    { label: 'Party / Customer:', val: payment.party_name || 'General' },
    { label: 'Payment Type:', val: payment.payment_type.toUpperCase().replace(/_/g, ' ') },
    { label: 'Payment Method:', val: payment.payment_method.toUpperCase().replace(/_/g, ' ') },
    payment.account_name ? { label: 'Account:', val: payment.account_name } : null,
    payment.reference_number ? { label: 'Ref / Txn No:', val: payment.reference_number } : null,
    payment.sale_number ? { label: 'Associated Sale:', val: payment.sale_number } : null,
    payment.particular ? { label: 'Particulars:', val: payment.particular } : null,
  ].filter(Boolean) as { label: string; val: string }[];

  rows.forEach((r) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(r.label, 14, y);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(r.val, 60, y);
    y += 7;
  });

  y += 4;
  // Amount Highlight Box
  doc.setFillColor(241, 245, 249);
  doc.rect(10, y, 128, 18, 'F');
  doc.setDrawColor(203, 213, 225);
  doc.rect(10, y, 128, 18, 'S');
  doc.setFillColor(...COLOR.gold);
  doc.rect(10, y, 1.4, 18, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text('TOTAL AMOUNT:', 16, y + 11);

  doc.setFontSize(13);
  doc.setTextColor(isIncome ? 16 : 185, isIncome ? 185 : 28, isIncome ? 129 : 28); // Emerald or Red
  doc.text(formatRupee(payment.amount), 132, y + 12, { align: 'right' });

  // Footer
  const pageHeight = 210;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(brandConfig.receiptFooterMessage, 74, pageHeight - 8, { align: 'center' });

  return doc;
}

/**
 * Triggers PDF download for a Sale Receipt.
 */
export function downloadSalesReceiptPdf(sale: SaleReceiptData): void {
  const doc = buildSalesReceiptPdf(sale);
  const cleanNum = sale.sale_number.replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`Klothiq_Receipt_${cleanNum}.pdf`);
}

/**
 * Triggers PDF download for a Payment Receipt.
 */
export function downloadPaymentReceiptPdf(payment: PaymentReceiptData): void {
  const doc = buildPaymentReceiptPdf(payment);
  const cleanNum = payment.receipt_number.replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`Klothiq_Payment_Receipt_${cleanNum}.pdf`);
}
